import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { FulfilmentActivityType } from "../use-get-fulfilment-activities/FulfilmentActivityType";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";

interface AddActivityRequest {
    levyId: string;
    notes?: string;
    taxPayerWorkspaceId: string;
    title: string;
    type?: FulfilmentActivityType;
    year: number;
}

export function useAddActivity() {
    const { t } = useTranslation();
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders({ method: "POST" });
    const queryClient = useQueryClient();

    const mutation = useMutation<boolean, Error, AddActivityRequest>({
        mutationFn: async (request) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments/${request.levyId}/years/${request.year}/tax-payers/${request.taxPayerWorkspaceId}/activities/create`,
                {
                    headers,
                    method: "POST",
                    body: JSON.stringify({
                        notes: request.notes,
                        title: request.title,
                        type: request.type,
                    }),
                },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }

            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["useGetFulfilmentActivities"],
            });
            queryClient.invalidateQueries({
                queryKey: ["useGetFulfilmentDetail"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetFulfilments"] });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
        },
        meta: {
            errorMessage: t("errors.messages.addActivity"),
        },
    });

    return mutation;
}
